/**
 * The runtime for a guided creation flow.
 *
 * Framework-free, like `TourEngine`: it walks a `TaskFlow`, keeps one target
 * resolved, and publishes a state object. The React layer above it draws a ring,
 * a callout and the bottom progress bar from that state and nothing else.
 *
 * 🔴 NO OVERLAY, AND THAT IS THE WHOLE DESIGN.
 * The user is filling in a real form: they need to click other fields, scroll the
 * dialog, open a Radix select whose list is portalled to `<body>`, and type with
 * arrow keys. A dimming layer would have to allow all of that back in one hole at
 * a time, and driver.js's `pointer-events: none` model cannot express it. So the
 * flow only ever ADDS chrome — a ring that does not take pointer events, a
 * callout, and a bar along the bottom. Everything underneath stays live.
 *
 * The cost of having no overlay is that nothing tells us when the page changed,
 * so the target is re-measured on a timer. See `TICK_MS`.
 */

import { isOutOfView, isReachable, resolveAnchor } from './anchors';
import { installBackdropGuard } from './backdropGuard';
import { restoreHostChrome, suppressHostChrome } from './hostChrome';
import { fillField, isFilled } from '../tasks/formFill';
import type { CalloutSide, TaskFlow, TaskStep } from '../tasks/types';

/**
 * How often the active target is re-measured.
 *
 * A polling loop rather than observers, because "has this step been satisfied?"
 * is an arbitrary predicate over the host's state — a value inside a React
 * component, a dialog that has closed — and no single MutationObserver or
 * ResizeObserver sees all of it.
 *
 * 🔴 This timer is NOT what keeps the ring aligned. It used to be, and that was
 * the bug: 140ms of lag is plainly visible on a scroll, so scroll and resize are
 * now handled by their own listeners (see `start`) and this loop is left to do only
 * what it is actually needed for — re-evaluating the predicate. Do not remove those
 * listeners on the grounds that the tick "already covers it".
 */
const TICK_MS = 140;

/**
 * How long to wait for a step's target to turn up before giving up on it.
 *
 * Generous because the step before it usually caused a route change or opened a
 * dialog, and both are asynchronous. An `optional` step that runs out is skipped;
 * a required one stops and says so, rather than silently pointing at nothing.
 */
const RESOLVE_TIMEOUT_MS = 5000;

/**
 * How long to hold the ring on a step's target BEFORE running its `prepare` —
 * the function that frequently INTERACTS with that very target (clicking a
 * button that opens the next screen, hiding a panel, …).
 *
 * 🔴 THIS HAS TO HAPPEN BEFORE `prepare`, NOT AFTER. `prepare` for a step like
 * `MESSAGE_TEMPLATE_TASK`'s "open-composer" clicks Create Template, and that
 * click is what OPENS THE COMPOSER DIALOG ON TOP OF IT. Resolving afterwards
 * still "finds" the button — `isVisible()` only checks CSS display/visibility,
 * not whether a modal is now covering it — so the ring drew a circle floating
 * over the dialog's own backdrop, around a button the user could no longer
 * see, for nothing they could connect to what had just happened. Resolving
 * and showing the ring BEFORE the click, and holding here just long enough to
 * register, is what makes "highlight the button, then click it" true in that
 * order rather than the reverse.
 */
const PRE_ACTION_HOLD_MS = 550;

/**
 * How long a single step's `rewind` gets before the engine stops waiting on it.
 *
 * 🔴 A CEILING ON BACK, NOT ON THE HOOK. Back is frozen while a rewind runs (see
 * `rewindThenEnter`), so a hook that never settles would be a Back button that
 * never responds — the exact complaint the hook exists to fix, in a new place.
 * The hook is not cancelled; the engine simply stops waiting and gets on with the
 * entry. Comfortably past `closeFlowChrome`'s own bounded budget (4 x 600ms), so a
 * rewind that is merely SLOW still finishes before this bites.
 */
const REWIND_TIMEOUT_MS = 3000;

/**
 * Marks the document while a flow runs.
 *
 * 🔴 NOTHING IN THIS MODULE'S CSS MAY SIZE THE HOST OFF IT. It used to carry one
 * rule — a `max-height` clamp on every open dialog, so the bar could not land on
 * a dialog's footer — and that clamp changed the dimensions of the app's own
 * popups for as long as a walkthrough ran. The bar moves around the dialog now
 * instead (`ui/barPlacement.ts`). The marker stays because "a flow is running"
 * is worth being able to see from outside the module, not because anything is
 * allowed to restyle the host through it.
 */
const ACTIVE_CLASS = 'tour-task-active';

/**
 * Least time between two corrective scrolls of the same target.
 *
 * The tick loop runs seven times a second; a correction that could fire on every
 * one of them would be a tug of war rather than a nudge.
 */
const NUDGE_INTERVAL_MS = 700;

/**
 * How long a scroll the USER made suppresses the engine's own.
 *
 * Looking away from the ring for a moment — to read the field above it, to check
 * what was just added — must not be undone under their hands. Long enough to be
 * a deliberate look, short enough that a target left off screen comes back.
 */
const USER_SCROLL_GRACE_MS = 1500;

/**
 * How long after our own `scrollIntoView` a `scroll` event still counts as ours.
 *
 * Without it the engine reads its own correction as the user scrolling and
 * suppresses the next one — a target that needs two corrections (a scroller
 * inside a scroller) would then only ever get the first.
 */
const SELF_SCROLL_WINDOW_MS = 300;

/**
 * Should the engine scroll the step's target back into view right now?
 *
 * Pure, and exported for its own tests: this is the whole "does the guide get to
 * move the page" policy, and both guards are the kind that only ever show up as
 * a complaint months later — a highlight that never comes back, or a view that
 * fights the user's own scrolling.
 *
 * `force` is ADOPTION: the first time a step's element is seen, showing it is the
 * entire point and neither guard may swallow it.
 */
export function shouldCorrectView({
  now,
  lastNudgeAt,
  userScrolledAt,
  force = false,
}: {
  now: number;
  lastNudgeAt: number;
  userScrolledAt: number;
  force?: boolean;
}): boolean {
  if (force) return true;
  // The tick loop runs seven times a second; a correction free to fire on every
  // one of them is a tug of war, not a nudge.
  if (now - lastNudgeAt < NUDGE_INTERVAL_MS) return false;
  // Looking away from the ring for a moment — to read the field above it, to
  // check what was just added — must not be undone under the user's hands.
  if (now - userScrolledAt < USER_SCROLL_GRACE_MS) return false;
  return true;
}

/**
 * Development-only trace of what each step resolved, filled and decided.
 *
 * 🔴 THIS EXISTS BECAUSE THE FAILURES HERE ARE INVISIBLE BY NATURE. Every way this
 * runtime can go wrong — an anchor that matches nothing, a fill the host form
 * reverts, a step quietly skipped on its resolve timeout — produces the SAME
 * outward symptom: a callout sitting next to an empty field. There is nothing to
 * see, so several rounds of reasoning-from-source guessed wrong about which of the
 * three it was. One line per decision turns all of them into something readable.
 *
 * `console.info` (not `debug`, which browsers hide behind a verbosity filter that
 * is off by default) and stripped from production by the `DEV` gate.
 */
const TRACE = Boolean(import.meta.env?.DEV);

function trace(...args: unknown[]): void {
  if (TRACE) console.info('[tour-task]', ...args);
}

/** A short, readable description of an element, for the trace. */
function describe(el: Element | null): string {
  if (!el) return 'null';
  const id = el.id ? `#${el.id}` : '';
  const testid = el.getAttribute('data-testid');
  const rect = el.getBoundingClientRect();
  return (
    `${el.tagName.toLowerCase()}${id}` +
    `${testid ? `[data-testid=${testid}]` : ''} ` +
    `${Math.round(rect.width)}x${Math.round(rect.height)}@${Math.round(rect.left)},${Math.round(rect.top)}`
  );
}

export interface TaskTargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export type TaskStepState = 'complete' | 'active' | 'upcoming' | 'skipped';

export interface TaskStepView {
  id: string;
  label: string;
  state: TaskStepState;
}

export interface TaskRunState {
  running: boolean;
  taskId: string | null;
  title: string;
  summary: string;
  /**
   * The flow's own completion copy — `TaskFlow.doneTitle`/`doneDescription`.
   *
   * 🔴 THIS WAS DECLARED ON EVERY TASK AND RENDERED BY NONE OF THEM until this
   * field existed. `TaskFlow.doneTitle`'s own doc comment says "Replaces the
   * step list once every step is done" — but nothing published it, so the
   * completion panel fell back to the generic `task.title` plus a bare
   * skipped/not-skipped line, and a flow's own specific next-step guidance
   * (e.g. "reopen it from the Draft filter") was authored and simply never
   * seen. Populated from the same source `title`/`summary` already are.
   */
  doneTitle: string;
  doneDescription: string;
  /**
   * True between the Try-it press and the first step appearing — i.e. while
   * `TaskFlow.beforeStart` is navigating.
   *
   * 🔴 THIS IS THE "NOTHING HAPPENS WHEN I PRESS TRY IT" FIX. `startTask`
   * calls `pauseForTask`, which destroys the tour popover, and only THEN does
   * `beforeStart` run — a sidebar click plus a route change plus a `lazy()`
   * chunk download, several seconds of it on a cold cache. Nothing was
   * published until `enter(0)`, so for that whole stretch the screen carried no
   * guide chrome whatsoever and the button read as broken. The bar now appears
   * immediately in this state, saying which module is being opened.
   *
   * `step` is null while it is true (there is no step yet), so the ring and
   * callout stay away — the bar is the only thing on screen, which is the right
   * amount of chrome for "wait a moment".
   */
  preparing: boolean;
  /** Copy for the preparing line — `TaskFlow.preparingLabel`, or a fallback. */
  preparingLabel: string;
  /** 0-based position, or `total` once the flow is finished. */
  index: number;
  total: number;
  /** Every step with its state, for the bottom bar. */
  steps: TaskStepView[];
  /** The active step's copy, or null when finished. */
  step: {
    id: string;
    title: string;
    description: string;
    side: CalloutSide;
    /** True when the user has to act before the flow moves on. */
    waiting: boolean;
    waitingHint: string | null;
    /**
     * Whether this step's goal is met.
     *
     * ADVISORY ONLY — it decides whether moving on counts as done or skipped, and
     * whether the hint is shown. It must NOT gate the Next button; see
     * `TaskEngine.next`.
     */
    satisfied: boolean;
    /** True when the step advances on its own — no Next button. */
    auto: boolean;
    /** Copy for the advance button, from `TaskStep.nextLabel`. Null means "Next". */
    nextLabel: string | null;
    /**
     * True when this step declares a `skipTo`, i.e. the callout should also offer a
     * secondary "Next" that jumps past this whole segment.
     */
    canSkipSegment: boolean;
    /** True once we have looked for the target and failed. */
    lost: boolean;
    /**
     * True while the step is still being SET UP on the screen it belongs to —
     * its target has not resolved yet and we have not given up on it either.
     *
     * 🔴 THIS IS "THE GUIDE APPEARS BEFORE THE PAGE DOES". A step is published
     * the instant it is entered, so the card was on screen describing a control
     * that had not rendered — over a half-loaded page, or over the previous
     * screen entirely, which is what the report calls the first guide showing
     * before the tour has started on that page. `preparing` only ever covered
     * `beforeStart`; every LATER navigation (Create flow → the builder canvas,
     * a heavy `lazy()` chunk behind it) had nothing at all.
     *
     * `TaskSpotlight` holds the card back while this is true. It goes false the
     * moment the target resolves, and `lost` (the existing 5s give-up) is what
     * stops it being a way to hide the guide forever — an anchorless step still
     * shows its card, it just does so once we know the anchor is not coming.
     */
    awaiting: boolean;
    /** Dim everything but the target, mirrored from `TaskStep.spotlight`. */
    spotlight: boolean;
  } | null;
  /** Where to draw the ring. Null while the target is unresolved. */
  target: TaskTargetRect | null;
  /**
   * The bounds of the dialog the target sits inside, when it does.
   *
   * 🔴 Published so the callout can be placed BESIDE the form instead of on top of
   * it. Anchoring the card to the field alone means that on a wide dialog the card
   * lands squarely over neighbouring inputs — it covers the very fields the user is
   * being walked through, which is the "UI is disturbed by the guide cards" report.
   * Knowing the dialog's extent is what lets the card step outside it.
   */
  avoid: TaskTargetRect | null;
  /** Every step done — the bar shows the completion panel. */
  finished: boolean;
  /**
   * How many steps were passed over rather than completed.
   *
   * Published so the completion panel can tell the truth. Reaching the end is not
   * the same as having done the work — Skip is on every step now, and the last
   * step is the one that actually creates the record, so a run that skipped it
   * finishes without having built anything. Announcing "everything is done" there
   * would be a plain falsehood the user can check against their own list.
   */
  skippedCount: number;
  /** True when the flow was left early, so the UI can offer the tour back. */
  exited: boolean;
}

/**
 * What the bar says while `beforeStart` runs.
 *
 * Deliberately not "Loading…": the user just pressed a button that promised to
 * take them somewhere, and naming the destination is what tells them the press
 * landed. Flows that do not name one fall back to a line that still says work
 * is in progress rather than to an empty bar.
 */
function preparingLabelFor(task: TaskFlow): string {
  return task.preparingLabel ?? 'Opening the module…';
}

export const IDLE_TASK_STATE: TaskRunState = {
  running: false,
  taskId: null,
  title: '',
  summary: '',
  doneTitle: '',
  doneDescription: '',
  preparing: false,
  preparingLabel: '',
  index: -1,
  total: 0,
  steps: [],
  step: null,
  target: null,
  avoid: null,
  finished: false,
  skippedCount: 0,
  exited: false,
};

export interface TaskEngineOptions {
  onState(state: TaskRunState): void;
  /** Called once when the final step completes. */
  onFinish?(taskId: string): void;
}

export class TaskEngine {
  private options: TaskEngineOptions;
  private task: TaskFlow | null = null;
  private index = -1;
  private timer: number | null = null;
  private suppressed: HTMLElement[] = [];

  /**
   * Teardown for the shield that makes the page behind an open dialog inert —
   * see `engine/backdropGuard.ts`. Held so `stop()` can take the listeners off
   * `document` again; a flow that ended must leave the app exactly as it found
   * it.
   */
  private releaseBackdrop: (() => void) | null = null;

  /** Steps the user got through, and ones skipped over. Kept for the bar. */
  private done = new Set<string>();
  private skipped = new Set<string>();

  /** Guards `enter` against the tick loop re-entering it mid-await. */
  private entering = false;

  /**
   * Set while `enter` is waiting for a step's target to appear, cleared the
   * moment it does (or the moment we stop waiting). Published as `step.awaiting`
   * — see its doc comment for what it is for.
   */
  private awaitingTarget = false;

  /** When the engine last corrected the view. See `bringIntoView`. */
  private lastNudgeAt = 0;

  /** When the USER last scrolled, as opposed to the engine. See `bringIntoView`. */
  private userScrolledAt = 0;

  /** Until when a `scroll` event is one the engine itself caused. */
  private selfScrollUntil = 0;

  /**
   * A Next pressed while the current step was still setting itself up.
   *
   * 🔴 THIS IS "THE NEXT BUTTON DOES NOTHING", AND IT IS A SECOND, INDEPENDENT WAY IN
   * TO IT. `TaskSpotlight` deliberately never disables Next (see its note) — but
   * `enter()` refuses to run while another entry is in flight, and `next()` ends in
   * `enter()`. A step whose `prepare` cannot find its target spends up to
   * `RESOLVE_TIMEOUT_MS` waiting for it plus however long `prepare`'s own polling
   * takes — around eleven seconds for a `clickWhenReady` + `waitUntil` pair that
   * never resolves — and EVERY press inside that window was dropped on the floor
   * with nothing shown and nothing logged. That is precisely the shape the template
   * flow got stuck in at "Write the header": a live-looking button, pressed several
   * times, doing nothing at all.
   *
   * Queuing the press instead means the worst case is a delay rather than a trap:
   * the tick loop honours it the moment entry finishes. Several presses collapse
   * into one advance, which is what someone pressing repeatedly means, and `enter()`
   * clears it so a press that has since been overtaken by Back or a segment skip
   * cannot fire into the wrong step.
   */
  private advanceQueued = false;

  /**
   * A Back or a Skip pressed in that same window.
   *
   * 🔴 THE QUEUE ABOVE ONLY EVER COVERED NEXT, AND THE OTHER TWO BUTTONS SIT
   * BESIDE IT IN THE SAME CALLOUT. `previous()` and `skip()` both end in
   * `enter()`, which refuses to run while another entry is in flight — so a
   * press inside the setup window was dropped exactly as Next's used to be, with
   * one difference that makes it read worse: `previous()` had already returned
   * `true`, so the provider did not fall back to leaving the flow either. The
   * button simply did nothing, for up to the several seconds a slow step spends
   * in `prepare`. That is the "Back and Skip do not work properly" half.
   *
   * Kept separate from `advanceQueued` rather than folded into one field,
   * because they are answers to different questions and the tick loop honours
   * this one FIRST: a user who pressed Back wants to go back, and collapsing it
   * into a generic "a move is pending" would let a stray Next win the race.
   * `enter()` clears both for the reason its own note gives.
   */
  private queuedMove: 'back' | 'skip' | null = null;

  /**
   * The index Back was last pressed FROM, while that Back still stands.
   *
   * 🔴 THIS IS "BACK GOES FORWARD", AND IT IS THE WORST-READING BUG IN THE FLOW.
   * Reproduced on the campaigns walkthrough: pressing Back on step 20
   * (`templates-tab`) landed the user on step 21 (`template-open`) — the step
   * AFTER the one they were on.
   *
   * Nothing about `previous()` moves forward. What happens is that it lands
   * correctly and the TICK LOOP then walks the run forward off that landing,
   * through two paths that know nothing about how the step was reached:
   *
   *  - a step whose target cannot be confirmed is skipped after
   *    `RESOLVE_TIMEOUT_MS`, and
   *  - an `advance: 'auto'` step that is already satisfied advances instantly.
   *
   * `previous()` picks its landing with `resolveAnchor` alone, but `resolve()`
   * additionally refuses a match that is occluded or whose `ready()` disagrees —
   * so Back can land on a step the engine then declines to show. On the
   * campaigns flow that cascades: the landing step is dropped, every drawer step
   * after it is dropped (the drawer closed when the campaign was sent),
   * `campaign-submit` is `auto` and satisfied by that same closed drawer so it
   * passes through instantly, and the run arrives at `template-open`.
   *
   * So a live Back puts a floor under the run: while this is set, NO automatic
   * forward move may reach it. The user asked to go back, and an engine
   * heuristic must not overrule that — it can leave a step standing with "this
   * control is not on screen" and Skip offered, which is honest, but it may not
   * carry the run past where the user just was.
   *
   * Cleared by any DELIBERATE forward move (Next, Skip, a segment skip) and by
   * `start`/`stop`, so it constrains exactly one gesture and never outlives it.
   */
  private backstopIndex: number | null = null;

  /** When the current step started looking for its target. */
  private lookingSince = 0;

  /**
   * The current step's anchor matched, but the match is sealed behind a modal.
   *
   * 🔴 THE DIFFERENCE BETWEEN "NOT THERE" AND "COVERED", AND THE TICK LOOP HAS TO
   * KNOW WHICH. A target that matched nothing is a broken step and is dropped
   * after `RESOLVE_TIMEOUT_MS`; a target that is merely behind a dialog THIS FLOW
   * opened is a step the user can still act on, and dropping it would auto-advance
   * the run five seconds after every Back press onto one — the "Back does nothing"
   * shape this file already warns about twice. See `resolve`.
   */
  private occluded = false;

  /** Element the active step is pointing at. */
  private el: HTMLElement | null = null;

  /** Last published signature, so an unchanged tick does not re-render React. */
  private signature = '';

  /** Scroll/resize handler, kept so it can be removed on teardown. */
  private onViewportChange: (() => void) | null = null;

  /**
   * Steps whose declared `fill` has landed, so it is applied once and then left
   * alone. Without this the engine would keep re-typing over the user's own edits
   * for as long as the step is on screen.
   */
  private filled = new Set<string>();

  private finished = false;
  private exited = false;

  constructor(options: TaskEngineOptions) {
    this.options = options;
  }

  get running(): boolean {
    return this.task !== null;
  }

  /**
   * Start a flow, replacing any flow already running.
   *
   * 🔴 IT USED TO RETURN FALSE WHEN ONE WAS ALREADY RUNNING, which turned a single
   * stuck flow into a permanently dead feature: the engine kept `task` set, so
   * every later attempt to open ANY flow was refused with no message anywhere —
   * the button simply stopped doing anything. Restarting is always the more useful
   * reading of "open this flow", and tearing the previous one down first is what
   * keeps its timer and its body class from leaking.
   */
  start(task: TaskFlow): boolean {
    if (this.task) this.stop();

    this.task = task;
    this.done = new Set();
    this.skipped = new Set();
    this.filled = new Set();
    this.finished = false;
    this.exited = false;
    this.awaitingTarget = false;
    this.lastNudgeAt = 0;
    this.userScrolledAt = 0;
    this.selfScrollUntil = 0;
    this.signature = '';
    // The sandbox badge is pinned bottom-left at the maximum z-index, which is
    // exactly where the progress bar goes. Same helper the tour uses.
    this.suppressed = suppressHostChrome();
    // 🔴 INSTALLED BEFORE ANY DIALOG THIS FLOW OPENS. Capture-phase listeners on
    // one node run in registration order, and `FloatingDrawer` adds its own when
    // it mounts — being here first is what lets the guard's
    // `stopImmediatePropagation` actually beat it. See `backdropGuard.ts`.
    this.releaseBackdrop = installBackdropGuard(() => this.el);
    document.body.classList.add(ACTIVE_CLASS);

    /**
     * 🔴 PUBLISHED BEFORE `beginFlow`, AND SYNCHRONOUSLY, so the bar is on
     * screen in the same frame as the click. Awaiting anything first — even one
     * microtask — puts it after the tour popover has already been torn down,
     * which is the flicker of bare page this is here to remove.
     *
     * `index` is pinned back to -1 so `tick()` (started just below, and running
     * every `TICK_MS` from now on) finds no step at that position and returns
     * without republishing over this state. A `start()` following a finished run
     * would otherwise still be carrying the old index.
     */
    this.index = -1;
    this.publishPreparing(task);

    void this.beginFlow(task);
    this.timer = window.setInterval(() => this.tick(), TICK_MS);

    /**
     * Re-measure the moment anything scrolls, instead of waiting for the tick.
     *
     * 🔴 CAPTURE PHASE, on the DOCUMENT. A `scroll` event does NOT bubble, and the
     * element being pointed at lives inside the dialog's own scroll container —
     * so a listener on `window` never hears the scroll that actually moved it.
     * Capturing at the document catches every scroller on the page, including that
     * one.
     *
     * Without this the ring lagged the target by up to `TICK_MS` on every scroll,
     * which on a long form is most of the time the user spends looking at it.
     * `passive` because we only ever read.
     */
    this.onViewportChange = (event?: Event) => {
      // 🔴 TELL THE TWO KINDS OF SCROLL APART. `bringIntoView` stands down for a
      // moment after the user scrolls, and every correction it makes fires this
      // same listener — so without the window below the engine would read its own
      // scroll as the user's and refuse to correct again.
      if (event?.type === 'scroll' && Date.now() >= this.selfScrollUntil) {
        this.userScrolledAt = Date.now();
      }
      if (!this.task || this.entering) return;
      this.resolve();
      this.publish(false);
    };
    document.addEventListener('scroll', this.onViewportChange, { capture: true, passive: true });
    window.addEventListener('resize', this.onViewportChange, { passive: true });

    return true;
  }

  /** Advance. Never refuses — an unsatisfied step is recorded as skipped. */
  next(): void {
    if (!this.task || this.finished) return;
    // Setting up, so `enter()` would refuse this and the press would vanish. Held
    // and replayed by `tick()` instead — see `advanceQueued`.
    if (this.entering) {
      this.advanceQueued = true;
      trace('  Next pressed while the step was still setting up — queued.');
      return;
    }
    const step = this.task.steps[this.index];
    if (!step) return;
    // A deliberate forward move ends the Back that was standing — the user has
    // changed direction, so the floor it put down goes with it.
    this.backstopIndex = null;
    void this.advanceFromNext(this.task, this.index, step);
  }

  /**
   * Runs a step's `onNext` (if it has one) before doing what `next()` always
   * did. Split out because `onNext` can await real work — a click that opens
   * a dialog — and the flow may have been stopped or stepped away from while
   * that was in flight, same guard as `beginFlow`/`enter`.
   */
  private async advanceFromNext(task: TaskFlow, index: number, step: TaskStep): Promise<void> {
    if (step.onNext) {
      try {
        await step.onNext();
      } catch (error) {
        // An action that fails is not a reason to strand the user on this
        // step forever — they can still do it by hand, same policy as
        // `prepare` and `beforeStart`.
        console.warn(`[tour-guide] task "${task.id}" step "${step.id}": onNext failed`, error);
      }
      if (this.task !== task || this.index !== index) return;
    }
    // 🔴 ALWAYS ADVANCES — it must never silently refuse. This used to return
    // early when the step's own `satisfied()` predicate was false, with the button
    // disabled to match. That made the walkthrough a SECOND validator on top of
    // the host form's own, and the two can disagree: a scoped selector that
    // misses, a control that renders a moment later, a value the form accepts and
    // the predicate does not. The result was a Next button that did nothing, with
    // no way for the user to tell whether the flow or the form was at fault.
    //
    // The form is the authority on whether the record can be created — its own
    // submit button is already disabled until it is happy. The walkthrough's job
    // is to explain and then get out of the way. An unsatisfied step is recorded
    // as SKIPPED rather than complete, so the bar says honestly that it was passed
    // over instead of claiming it was done.
    if (this.isSatisfied(step)) this.done.add(step.id);
    else this.skipped.add(step.id);
    void this.enter(index + 1);
  }

  /**
   * Step back. The previous step's `prepare` runs again — see `fillField`.
   *
   * Returns FALSE when there is nothing earlier to land on, which is a real
   * answer rather than a failure: the caller uses it to leave the flow and take
   * the tour back a stop instead. Without that, Back on the first step — or on
   * a step whose every predecessor is a pass-through (see below) — was simply
   * dead, which is indistinguishable from a broken button.
   */
  previous(): boolean {
    if (!this.task) return false;

    // Setting up, so `enter()` would refuse this and the press would vanish —
    // and `true` here is what stops the provider reading a dropped press as
    // "nowhere to go" and ending the flow. Replayed by `tick()`.
    if (this.entering) {
      this.queuedMove = 'back';
      trace('  Back pressed while the step was still setting up — queued.');
      return true;
    }

    /**
     * 🔴 STEP OVER AN ALREADY-SATISFIED `auto` STEP — landing on one is not a
     * real stop. `enter()` re-checks `advance === 'auto' && isSatisfied()`
     * the instant a step is entered, from EITHER direction, and an `auto`
     * step whose action already happened (the composer is open, the AI
     * panel is already hidden) is still satisfied when re-entered. Without
     * this, Back on the step just past one instantly re-advanced past it
     * again — the callout flashed back and immediately forward, reading as
     * "the Back button does nothing." Auto steps are pass-through in both
     * directions: this walks past as many of them as are already done,
     * stopping at the first step that is either manual or not yet satisfied.
     */
    let target = this.index - 1;
    while (target >= 0) {
      const step = this.task.steps[target];
      if (step.advance === 'auto' && this.isSatisfied(step)) {
        target -= 1;
        continue;
      }
      /**
       * 🔴 AND STEP OVER A STEP WHOSE TARGET IS NOT ON SCREEN, for the same
       * reason: landing on one is not a real stop either.
       *
       * `tick()` already drops a step whose anchor matches nothing — but only
       * after `RESOLVE_TIMEOUT_MS` of sitting on it with the callout reading
       * "This control is not on screen". Back walking into a form that has since
       * been SUBMITTED hits that on every step of it in turn: the campaign
       * drawer closes on send, so pressing Back once from the step after it used
       * to spend five seconds per drawer field, in order, before finally landing
       * somewhere real — which reads as the button being broken rather than as
       * the flow unwinding.
       *
       * Skipping them here changes nothing about WHICH step Back lands on, only
       * how long it takes to get there: the engine was always going to pass over
       * every one of them. A step whose `prepare` would re-create its own target
       * (the send-options popover, the collapsed Advanced Settings panel) is
       * still stepped over — that is the right call, because the step BEFORE it
       * is the one that put the panel on screen, and that is where the user
       * pressing Back is actually trying to get back to.
       *
       * When nothing earlier resolves at all, `target` runs off the start and the
       * documented fall-out-of-the-flow answer below applies unchanged.
       */
      /**
       * 🔴 `isReachable`, NOT JUST "DID THE ANCHOR MATCH" — the two disagree,
       * and the gap between them is what sent Back FORWARDS.
       *
       * `resolveAnchor` accepts anything `isVisible` accepts, which is a pure
       * CSS test, so it says yes to a control sitting behind a modal. `resolve()`
       * — the thing that actually decides whether a step can be SHOWN — refuses
       * that same match (see its own note on the blurred highlight). So a walk
       * that asked only `resolveAnchor` could land on a step the engine then
       * declined to display: `this.el` stayed null, and `RESOLVE_TIMEOUT_MS`
       * later the tick loop skipped it forward. On the campaigns flow that
       * cascade ran all the way from step 20 to step 21 (see `backstopIndex`).
       *
       * Asking the same question `resolve()` asks means Back lands somewhere it
       * can actually point at. `backstopIndex` remains the safety net for the
       * case where it still cannot.
       */
      const hit = resolveAnchor(step.anchor);
      if (!hit || !isReachable(hit.element)) {
        target -= 1;
        continue;
      }
      break;
    }

    /**
     * 🔴 RUNNING OFF THE START IS NOT A REASON TO END THE WALKTHROUGH. It was,
     * and that is the "Back exits the tour guide entirely" report: `false` sends
     * the caller to `endTask('back')` (see `TourGuideProvider.taskPrevious`), so
     * a single Back from the middle of a flow tore the whole thing down.
     *
     * The walk above skips a step for TWO different reasons and only one of them
     * is fatal:
     *
     *  - `advance: 'auto'` AND satisfied — MUST be stepped over. `enter()`
     *    re-checks that on entry and would advance straight forward again, so
     *    landing there is a guaranteed flash-back-and-forward.
     *  - its anchor does not resolve — stepped over only to save the user five
     *    seconds of "This control is not on screen". Landing there is untidy,
     *    NOT broken: the step's own `prepare` frequently re-creates its target
     *    (a popover, a collapsed panel), and where it does not, the callout says
     *    so and Skip step is right there.
     *
     * So a second pass relaxes the second reason and keeps the first. On the
     * form-builder flow that is the whole difference: with the drawer open,
     * `open-composer`'s target is the Forms page's own New form button, so a
     * Back from "Name it" walked past it, ran off the start, and closed the
     * walkthrough instead of going back one step.
     */
    if (target < 0) {
      target = this.index - 1;
      while (target >= 0) {
        const step = this.task.steps[target];
        if (step.advance === 'auto' && this.isSatisfied(step)) {
          target -= 1;
          continue;
        }
        break;
      }
    }

    /**
     * Only now is `false` the honest answer: every step behind this one is a
     * pass-through that would immediately hand control forward again, so there
     * is genuinely nowhere inside the flow to go. That is also the case Back on
     * the FIRST step has always been, and the caller's leave-the-flow behaviour
     * is right for both.
     */
    if (target < 0) return false;

    // 🔴 `skipped` IS CLEARED TOO, not just `done`. Going back to redo a step
    // that was passed over and then completing it left it marked skipped for
    // the rest of the run — so the completion panel warned "N steps skipped, so
    // this may not have been created" about work the user had just gone back
    // and done. The two sets are the same bookkeeping and have to be rewound
    // together.
    this.task.steps.slice(target).forEach((s) => {
      this.done.delete(s.id);
      this.skipped.delete(s.id);
    });
    this.finished = false;
    /**
     * 🔴 SET BEFORE `enter`, AND FROM THE INDEX BACK WAS PRESSED ON — not from
     * the landing. This is the floor that stops the tick loop walking the run
     * forward off a landing it cannot show and carrying it PAST where the user
     * just was. See `backstopIndex` for the cascade it prevents.
     */
    this.backstopIndex = this.index;
    // 🔴 NOT A BARE `enter(target)` ANY MORE — see `rewindThenEnter`. The steps
    // being stepped back over undo themselves FIRST, or Back lands on a step whose
    // target is behind the composer a later step opened.
    void this.rewindThenEnter(this.task, this.index, target);
    return true;
  }

  /**
   * Undo the steps being stepped back over, then enter the one being landed on.
   *
   * 🔴 THIS IS "BACK DISTURBS THE FLOW AND JUST SHOWS THE PAGE", and the other
   * half of `TaskStep.rewind` — read its doc comment for the why. Backwards is the
   * one direction where a step's target is routinely covered by something a LATER
   * step opened, and `resolve()` refuses to ring a covered target (correctly: the
   * ring would land on an empty strip of the dialog now sitting over it). What
   * refusing leaves behind is a step standing with NO ring, its callout parked
   * wherever there was room. Undoing the layer is what makes the target real
   * again.
   *
   * Runs in REVERSE, from the step being left down to and INCLUDING the one being
   * landed on. Including it is the point: its own `onNext` already ran on the way
   * forward, so its effect is precisely what stands between the user and the
   * screen that step describes.
   *
   * 🔴 `entering` IS HELD FOR THE WHOLE REWIND. `tick()` bails while it is set,
   * which stops the loop auto-advancing off — or timing out on — the step being
   * left, during a window where the page is deliberately mid-teardown. It is
   * cleared with NOTHING awaited between there and `enter()`, or the very flag
   * protecting the entry would make `enter()` refuse it.
   */
  private async rewindThenEnter(task: TaskFlow, from: number, target: number): Promise<void> {
    this.entering = true;

    for (let i = from; i >= target; i -= 1) {
      const step = task.steps[i];
      if (!step?.rewind) continue;
      trace(`  rewinding "${step.id}" on the way back to step ${target + 1}.`);
      try {
        await this.bounded(step.rewind(), REWIND_TIMEOUT_MS);
      } catch (error) {
        // Same rule as `prepare` and `onNext`: a hook that throws is not a reason
        // to strand the user mid-flow. The entry below still happens, and the step
        // it lands on already says for itself when its target is not on screen.
        console.warn(`[tour-guide] task "${task.id}" step "${step.id}": rewind failed`, error);
      }
      // Stopped or replaced while we awaited. `stop()` has already cleared
      // `entering` (and `start()` stops first), so leaving the flag ALONE here is
      // what keeps this stale call from unsetting one the next flow now owns.
      if (this.task !== task) return;
    }

    if (this.task !== task) return;
    this.entering = false;

    /**
     * A press that landed DURING the rewind. `enter()` clears `queuedMove` on the
     * way in — right for a press belonging to the step being left, wrong for this
     * one, which was made after the user had already committed to this move. The
     * `void` call below runs synchronously as far as `enter()`'s first await, by
     * which point that clearing has happened, so putting the press back afterwards
     * is enough for the tick loop to replay it once this entry finishes. Without
     * it, a double-press of Back went back one step.
     */
    const pressedDuringRewind = this.queuedMove;
    void this.enter(target);
    if (pressedDuringRewind) this.queuedMove = pressedDuringRewind;
  }

  /**
   * Wait on a flow-authored hook, but never longer than `ms`.
   *
   * The hook is not cancelled — nothing here can cancel one — it is simply no
   * longer waited on, which is the same trade `TourGuideProvider` makes for
   * `afterEnd`.
   */
  private bounded(work: void | Promise<void>, ms: number): Promise<void> {
    return Promise.race([
      Promise.resolve(work),
      new Promise<void>((resolve) => window.setTimeout(resolve, ms)),
    ]);
  }

  /**
   * Jump past this step's whole SEGMENT — to the step its `skipTo` names, or out of
   * the flow entirely when that is `'end'`.
   *
   * 🔴 EVERY STEP PASSED OVER IS RECORDED AS SKIPPED, including this one. Nothing
   * between here and the landing step ran, so counting any of them as done would
   * hand the completion panel a clean bill of health for work that never happened —
   * and that panel's skipped-count warning is what stops a bailed-out run from
   * claiming it created a template and a campaign.
   *
   * Returns false when the flow should be ENDED by the caller instead (`'end'`, or a
   * target id that no longer exists — a flow edited without updating its own
   * pointer). The engine cannot end-and-resume-the-tour by itself: that is the
   * provider's `endTask`, which owns the resume token.
   */
  skipSegment(): boolean {
    if (!this.task || this.finished) return true;
    const step = this.task.steps[this.index];
    const target = step?.skipTo;
    if (!target) return true;

    const landing = target === 'end'
      ? -1
      : this.task.steps.findIndex((s, i) => i > this.index && s.id === target);

    if (landing < 0) {
      if (target !== 'end' && import.meta.env.DEV) {
        console.warn(
          `[tour-guide] task "${this.task.id}" step "${step?.id}": skipTo "${target}" ` +
            'is not a later step in this flow — ending the flow instead.',
        );
      }
      // Mark the rest skipped so the bar cannot report a clean run.
      this.task.steps.slice(this.index).forEach((s) => this.skipped.add(s.id));
      this.publish(true);
      return false;
    }

    this.task.steps.slice(this.index, landing).forEach((s) => this.skipped.add(s.id));
    // Deliberate forward move — see `next()`.
    this.backstopIndex = null;
    void this.enter(landing);
    return true;
  }

  /** Leave this step out and carry on. Always available. */
  skip(): void {
    if (!this.task || this.finished) return;
    // Same window, same fix as Next and Back: `enter()` refuses while another
    // entry is in flight, and a Skip pressed there used to be dropped silently.
    // A stuck `auto` step is exactly when this button gets pressed, and exactly
    // when the engine is most likely to be mid-entry.
    if (this.entering) {
      this.queuedMove = 'skip';
      trace('  Skip pressed while the step was still setting up — queued.');
      return;
    }
    const step = this.task.steps[this.index];
    if (step) this.skipped.add(step.id);
    // Deliberate forward move — see `next()`.
    this.backstopIndex = null;
    void this.enter(this.index + 1);
  }

  /**
   * Stop the flow.
   *
   * `exited` distinguishes "left early" from "finished", because the caller
   * offers the interrupted tour back in both cases but should not claim the user
   * built something when they did not.
   */
  stop(): void {
    if (!this.task) return;
    if (!this.finished) this.exited = true;
    this.clearTimer();
    restoreHostChrome(this.suppressed);
    this.suppressed = [];
    this.releaseBackdrop?.();
    this.releaseBackdrop = null;
    document.body.classList.remove(ACTIVE_CLASS);
    this.task = null;
    this.index = -1;
    this.el = null;
    this.entering = false;
    this.awaitingTarget = false;
    this.advanceQueued = false;
    this.queuedMove = null;
    this.backstopIndex = null;
    this.signature = '';
    this.options.onState({ ...IDLE_TASK_STATE, exited: this.exited });
  }

  destroy(): void {
    this.stop();
  }

  /* ---------------------------------------------------------------- *
   * Step transitions.
   * ---------------------------------------------------------------- */

  /**
   * Run a task's silent `beforeStart`, then enter its first step.
   *
   * Split out of `start()` (which stays synchronous) so a caller of `start()`
   * gets its `true`/`false` immediately rather than waiting on navigation.
   * Nothing is published here — no ring, no bottom bar — until `enter(0)`
   * runs; that gap is what a page navigation with no guide chrome yet looks
   * like, which is the point of moving setup out of the visible steps.
   */
  private async beginFlow(task: TaskFlow): Promise<void> {
    try {
      await task.beforeStart?.();
    } catch (error) {
      // Setup that fails is not a reason to abandon the flow — the user can
      // still get there by hand, and the first real step's own `prepare` (or
      // the user themselves) can pick up from wherever that leaves them.
      console.warn(`[tour-guide] task "${task.id}": beforeStart failed`, error);
    }
    // The flow may have been stopped or replaced while we awaited setup.
    if (this.task !== task) return;
    void this.enter(0);
  }

  private async enter(index: number): Promise<void> {
    const task = this.task;
    if (!task || this.entering) return;

    if (index >= task.steps.length) {
      this.index = task.steps.length;
      this.finished = true;
      this.el = null;
      this.publish(true);
      this.options.onFinish?.(task.id);
      return;
    }

    this.entering = true;
    // Whatever was queued belonged to the step we are leaving; carrying it into this
    // one would advance twice off a single press — or, for a queued Back, walk on
    // past the step it was pressed to reach.
    this.advanceQueued = false;
    this.queuedMove = null;
    try {
      this.index = index;
      this.el = null;
      // Is this step's screen here YET? See `step.awaiting`.
      //
      // 🔴 A PROBE, NOT AN UNCONDITIONAL `true`. `this.el` is deliberately null
      // at this point and `resolve()` has not run, so assuming "awaiting" would
      // hide the card for one tick on EVERY step — including the ordinary ones
      // whose target is already sitting there — and a guide that blinks out at
      // each transition is a worse bug than the one this is fixing. Asking the
      // anchor directly costs one `querySelector` and is true only when the
      // screen genuinely has not arrived.
      //
      // Set BEFORE the first `publish` below, which is what keeps even that
      // first frame from showing an unanchored card.
      this.awaitingTarget = !resolveAnchor(task.steps[index].anchor);
      // Belongs to the step being left. `resolve()` rewrites it on its next call
      // (the tick loop's first act), but an explicit reset keeps a stale `true`
      // from suppressing the drop of a genuinely missing target for one tick.
      this.occluded = false;
      this.lookingSince = Date.now();
      const step = task.steps[index];
      trace(
        `step ${index + 1}/${task.steps.length} "${step.id}"`,
        `advance=${step.advance ?? 'manual'}`,
        `satisfiedOnEntry=${this.isSatisfied(step)}`,
        `anchorResolvesOnEntry=${describe(resolveAnchor(step.anchor)?.element ?? null)}`,
      );

      // Publish before resolving so the bar moves the instant the user
      // clicks, rather than after whatever `prepare` has to do.
      this.publish(true);

      // 🔴 WAIT FOR — AND SHOW — THE TARGET BEFORE `prepare` RUNS, not after,
      // and not just ONCE. `prepare` is frequently what INTERACTS with this
      // very target: clicking a button that opens the next screen, hiding a
      // panel, and so on. Two separate bugs live here:
      //
      //  1. Resolving only AFTER `prepare` runs means the ring is drawn
      //     against whatever is left once the interaction already happened —
      //     for a button that opens a dialog on top of itself, `isVisible()`
      //     (a plain CSS display/visibility check) still "finds" it, so the
      //     ring drew a circle floating over the dialog's own backdrop,
      //     around a button the user could no longer see.
      //  2. A SINGLE, IMMEDIATE `resolve()` call only ever notices a target
      //     that is ALREADY on screen. The step right after a navigation —
      //     `beforeStart`'s, or the previous step's own `prepare` — has
      //     nothing to find for however long that page takes to render.
      //     Without WAITING here, `prepare`'s own polling (`clickWhenReady`)
      //     ends up being the first and only code to ever see the target,
      //     and it clicks it the instant it appears with nothing shown first
      //     — which is exactly how "Try it: build a template" never
      //     highlighted Create Template: the templates page had not finished
      //     rendering yet when this used to resolve just once.
      //
      // Skipped when the step is ALREADY satisfied (formVisible()-style
      // steps whose `prepare` is a no-op) or has no `prepare` at all —
      // neither is about to interact with anything, so there is nothing
      // worth waiting or holding for.
      //
      // 🔴 UNLESS `step.forceHighlight` SAYS OTHERWISE — see its doc comment
      // on `TaskStep`. Some steps can be `alreadySatisfied` on the very first
      // entry for a reason that has nothing to do with anything having been
      // shown yet (a native default, or a host form that already held a
      // value from an earlier run), and skipping the highlight for THOSE
      // reads as "this step never appeared at all".
      const alreadySatisfied = this.isSatisfied(step);
      if (step.prepare && (!alreadySatisfied || step.forceHighlight)) {
        await this.waitForTarget(step, RESOLVE_TIMEOUT_MS);
        if (this.task !== task || this.index !== index) return;
        if (this.el) {
          // Give the user (and the automatic click about to happen) a beat
          // to actually register the highlighted target before `prepare`
          // acts on it.
          await this.sleep(PRE_ACTION_HOLD_MS);
          if (this.task !== task || this.index !== index) return;
        } else {
          // Always-on, not DEV-gated: `prepare` is about to run anyway (its
          // own `clickWhenReady` may resolve a DIFFERENT, unscoped match than
          // what `ready()` refused here), so this is the one place that can
          // say WHY the highlight never appeared before that happens.
          console.warn(
            `[tour-guide] task "${task.id}" step "${step.id}": no target confirmed within ` +
              `${RESOLVE_TIMEOUT_MS}ms (anchor resolved but ready() never agreed, or nothing ` +
              `resolved at all) — proceeding to prepare() without a highlight.`,
          );
        }
      } else {
        this.resolve();
        this.publish(true);
      }

      try {
        await step.prepare?.();
      } catch (error) {
        // A step that cannot set itself up is not a reason to end the flow: the
        // user can still do it by hand, which is what the copy asks for anyway.
        console.warn(`[tour-guide] task "${task.id}" step "${step.id}": prepare failed`, error);
      }

      // 🔴 A STEP WITH A `prepare` HAS HAD ITS CHANCE BY NOW. It was given the
      // full `RESOLVE_TIMEOUT_MS` above and then its `prepare` ran, so an
      // unresolved target here is a step that legitimately has nothing to point
      // at (`run-test`'s Focus button on a clean flow is the standard case) —
      // and `lookingSince` has just been restarted for the tick loop, so `lost`
      // would hold the card back for another five seconds for nothing.
      if (step.prepare) this.awaitingTarget = false;

      trace(
        `  after prepare "${step.id}"`,
        `satisfied=${this.isSatisfied(step)}`,
        `anchor=${describe(resolveAnchor(step.anchor)?.element ?? null)}`,
      );

      /**
       * 🔴 RESTART THE RESOLVE CLOCK. `prepare` now does real, awaited work —
       * `clickWhenReady` polls for up to four seconds — but `lookingSince`
       * was stamped BEFORE it ran, against a budget of five. So a step that spent four seconds waiting for its dialog had one
       * second left to find its target, and the tick loop then skipped it as
       * "not on screen" almost immediately.
       *
       * The visible result was exactly the two symptoms reported together: fields
       * were filled and then abandoned unpointed-at (so the dummy data looked like
       * it never arrived), and the callout raced ahead to steps whose targets were
       * not there yet (so the guide was describing one thing while the ring was
       * somewhere else). The clock has to start when we ACTUALLY begin looking.
       */
      this.lookingSince = Date.now();

      // First attempt at the declared dummy data. The tick loop keeps trying.
      if (this.task === task) this.applyFill(step);

      // Re-publish AFTER prepare.
      //
      // `prepare` is what types the dummy data in, so the state published before
      // it ran still says the field is empty. Without this the callout keeps
      // showing "type a name to carry on" — under a field that visibly already has
      // a name in it — until the next tick happens to notice. One frame of
      // self-contradiction is enough to read as broken.
      if (this.task === task) this.publish(true);

      // Already done — the field was pre-filled, the panel was already hidden,
      // the user is already on the page, or this step's own `prepare` just
      // satisfied it. The target was already resolved and shown ABOVE, before
      // `prepare` ran, so it is safe to move on without flashing anything
      // further here.
      if (this.task === task && step.advance === 'auto' && this.isSatisfied(step)) {
        this.done.add(step.id);
        this.entering = false;
        await this.enter(index + 1);
        return;
      }

      if (this.task === task) this.resolve();
    } finally {
      this.entering = false;
    }
  }

  /** Resolve after `ms` — used to hold a step's highlight before its `prepare` acts on it. */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  /**
   * Poll for the ACTIVE step's anchor to turn up, publishing the instant it
   * does, instead of `resolve()`'s single, immediate check.
   *
   * Frame-driven (same shape as `clickWhenReady` in `formFill.ts`) so a target
   * that is already there costs nothing, and one that only exists once a
   * navigation finishes rendering is still caught the moment it mounts,
   * rather than missed by a resolve that ran one tick too early.
   *
   * 🔴 A RESOLVED ELEMENT IS NOT ACCEPTED UNTIL `step.ready?.()` ALSO AGREES.
   * `anchor` matches on shape alone: a button identified only by its own
   * label can resolve against an identically-labelled button on a DIFFERENT
   * page — e.g. Create Template exists on both the templates library and the
   * Overview dashboard. Without this gate, a step waiting to navigate
   * somewhere would find and click the wrong page's button on its very first
   * poll, before any navigation had even begun, and the flow would "arrive"
   * having never actually gone anywhere. `this.el` is left set to whatever
   * resolved (so a still-pending navigation keeps polling from a warm
   * reference rather than losing it), but nothing is published or accepted
   * until `ready()` is also true.
   */
  private async waitForTarget(step: TaskStep, timeoutMs: number): Promise<void> {
    const startedAt = Date.now();
    while (this.task) {
      this.resolve();
      if (this.el && (step.ready?.() ?? true)) {
        this.publish(true);
        return;
      }
      if (Date.now() - startedAt >= timeoutMs) return;
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
    }
  }

  /* ---------------------------------------------------------------- *
   * The loop.
   * ---------------------------------------------------------------- */

  private tick(): void {
    const task = this.task;
    if (!task || this.entering || this.finished) return;

    // A press held while the step was setting itself up. Honoured first, before any
    // of the auto-advance or resolve-timeout paths below can decide the same step's
    // fate — the user asked, and the engine's own guesses come second.
    //
    // 🔴 BACK AND SKIP ARE REPLAYED BEFORE NEXT. All three can be queued, and a
    // step that finishes setting up may well satisfy itself in the same frame —
    // so honouring a queued Next first would carry a user who pressed Back
    // FORWARD instead. The direction the user chose wins over the direction the
    // flow was already going.
    if (this.queuedMove) {
      const move = this.queuedMove;
      this.queuedMove = null;
      // `previous()` answering false here means the flow has nowhere earlier to
      // go, which is the caller's cue to leave — but the caller is not on the
      // stack any more, so the honest thing is to stay put rather than tear the
      // flow down from inside a timer the user did not press.
      if (move === 'back') this.previous();
      else this.skip();
      return;
    }

    if (this.advanceQueued) {
      this.advanceQueued = false;
      this.next();
      return;
    }

    const step = task.steps[this.index];
    if (!step) return;

    this.resolve();
    this.applyFill(step);

    if (step.advance === 'auto' && this.isSatisfied(step)) {
      if (this.blockedByBackstop(this.index + 1)) return;
      this.done.add(step.id);
      void this.enter(this.index + 1);
      return;
    }

    // Nothing to point at, and we have waited long enough — but NOT when the
    // target is merely covered (see `occluded`): that step is still real, and
    // auto-advancing off it is indistinguishable from Back being ignored.
    if (!this.el && !this.occluded && Date.now() - this.lookingSince > RESOLVE_TIMEOUT_MS) {
      const optional = step.optional ?? true;
      if (optional && !this.blockedByBackstop(this.index + 1)) {
        trace(
          `  SKIPPING "${step.id}" — its anchor matched nothing for ${RESOLVE_TIMEOUT_MS}ms.`,
          'This is the path that makes a filled field look unfilled: the step is',
          'dropped and never pointed at. Check the selector against the live DOM.',
        );
        this.skipped.add(step.id);
        void this.enter(this.index + 1);
        return;
      }
      // Required and missing: fall through and publish, which renders the
      // "cannot find it" state with Skip still offered.
    }

    this.publish(false);
  }

  /**
   * Would moving to `next` cross the floor a live Back put down?
   *
   * Only ever consulted by the tick loop's two AUTOMATIC forward paths — a
   * deliberate Next or Skip clears the backstop before it moves, so this can
   * never stand in the user's way. See `backstopIndex`.
   */
  private blockedByBackstop(next: number): boolean {
    return this.backstopIndex !== null && next >= this.backstopIndex;
  }

  /** Re-resolve the active step's element, scrolling to it the first time. */
  private resolve(): void {
    const step = this.task?.steps[this.index];
    if (!step) return;

    const hit = resolveAnchor(step.anchor);
    /**
     * 🔴 A MATCH BEHIND A MODAL IS NOT A TARGET, AND THIS IS THE REPORTED BUG:
     * "it flows back but the highlight is blurred."
     *
     * `resolveAnchor` accepts anything `isVisible` accepts, which is a pure CSS
     * test — so a control covered by a dialog still matches. Going FORWARD that
     * rarely shows, because a step's target is covered by something a LATER step
     * opens. Going BACK it is the normal case: reproduced in a real browser on the
     * campaigns flow, pressing Back from "Name the campaign" landed on "Start a
     * campaign", whose `New Campaign` button is behind the composer the previous
     * step opened — so the ring and its spotlight hole were drawn at the button's
     * coordinates, which is now an empty strip of the WhatsApp preview panel.
     * Everything the user was actually looking at stayed dimmed.
     *
     * Refusing the match means no ring rather than a wrong one. The step still
     * stands (see `occluded`), and the moment the covering layer goes away the
     * next tick resolves it normally.
     */
    const found = hit && isReachable(hit.element) ? hit.element : null;
    this.occluded = Boolean(hit) && !found;

    if (found && found !== this.el) {
      this.el = found;
      // The screen this step describes is here. Whatever the rest of the entry
      // is still doing, the guide can be shown against something real now.
      this.awaitingTarget = false;
      // 🔴 INSTANT, NOT SMOOTH. A smooth scroll animates for a few hundred
      // milliseconds, and the rect is only re-measured every `TICK_MS` — so for the
      // whole animation the ring and the callout were drawn from a position the
      // target had already left. That is the "the guide is somewhere and the field
      // is somewhere" report: not a positioning miscalculation, a race against an
      // animation. Jumping straight there means the very next measurement is
      // already correct.
      // 🔴 FORCED: ADOPTING A TARGET IS NOT A CORRECTION. This is the first
      // time the step's element has been seen, so showing it to the user is the
      // whole point — it must not be dropped by the rate limit or by the
      // user-scroll grace that exist to stop the CORRECTIVE path nagging.
      this.bringIntoView(found, { force: true });
      return;
    }

    /**
     * 🔴 AND KEEP IT THERE. Adopting the target once is not enough when the page
     * grows UNDER it: every field the create-form flow adds pushes the "Add
     * block" button it is ringing further down the drawer's scroller, and by the
     * third one the button sits at `y: 798` against a scrollport that ends at
     * `726` — off screen, with the ring drawn over the drawer's footer around
     * nothing. Reported as the highlight not landing on the step's element, and
     * the element being left where it is instead of scrolled to.
     *
     * Only when it is properly GONE (see `isOutOfView`), and rate-limited, so
     * this cannot turn into the engine dragging the view back on every tick.
     */
    if (found) {
      if (isOutOfView(found)) this.bringIntoView(found);
      return;
    }

    /**
     * 🔴 AN ALREADY-ADOPTED TARGET IS RELEASED WHEN IT GETS COVERED, and this is
     * the half a "refuse the match" fix alone silently misses. Refusing only stops
     * a covered element being ADOPTED — but the usual way a target becomes covered
     * is a dialog opening while the step is already on screen, which is exactly
     * what `prepare`/`onNext` do. Without this the ring keeps being drawn from the
     * element the engine took a moment ago, at coordinates the modal has since
     * put something else at.
     *
     * Deliberately ahead of the tolerance below: that one keeps a still-connected
     * element through a resolver MISS (a re-render between frames, where "no
     * match" means "not this frame"). An occlusion is not a miss — it is a
     * definite answer about a match we actually found — so the tolerance must not
     * swallow it.
     */
    if (this.occluded) {
      this.el = null;
      return;
    }

    // Keep a still-connected element even when the resolver momentarily misses
    // it (a re-render between frames), but drop one that has genuinely gone.
    if (this.el && !this.el.isConnected) this.el = null;
  }

  /**
   * Scroll the step's target back into view.
   *
   * 🔴 INSTANT, NOT SMOOTH. A smooth scroll animates for a few hundred
   * milliseconds and the rect is only re-measured every `TICK_MS`, so for the
   * whole animation the ring and the callout are drawn from a position the
   * target has already left — the "the guide is somewhere and the field is
   * somewhere" report, which is a race against an animation rather than a
   * placement miscalculation. Jumping straight there makes the very next
   * measurement correct.
   *
   * 🔴 AND IT NEVER FIGHTS THE USER. Two guards, both load-bearing: a target
   * that is only just out of view is left alone (`isOutOfView` answers "gone",
   * not "clipped"), and a scroll the USER just made suppresses the next one for
   * `USER_SCROLL_GRACE_MS` — otherwise looking away from the ring for a moment
   * would be undone under their hands, which is worse than a highlight they have
   * to scroll back to. `NUDGE_INTERVAL_MS` keeps even the legitimate case to a
   * correction rather than a tug of war.
   */
  private bringIntoView(el: HTMLElement, { force = false }: { force?: boolean } = {}): void {
    const now = Date.now();
    const allowed = shouldCorrectView({
      now,
      lastNudgeAt: this.lastNudgeAt,
      userScrolledAt: this.userScrolledAt,
      force,
    });
    if (!allowed) return;

    this.lastNudgeAt = now;
    // The scroll below fires `scroll` events of our own; without this window the
    // listener would read them as the user scrolling and suppress the next
    // correction for a second and a half.
    this.selfScrollUntil = now + SELF_SCROLL_WINDOW_MS;
    try {
      el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
    } catch {
      // Older engines reject the options object; position is a nicety here.
      el.scrollIntoView();
    }
  }

  /**
   * Apply a step's declared dummy data, retrying until it sticks.
   *
   * Called from the tick loop as well as on entry, which is what makes it immune to
   * the three timing failures documented on `TaskStep.fill`: a field that mounts
   * late, one that is briefly `disabled`, and a write the host form reverts. Each
   * simply gets another attempt 140ms later.
   *
   * A field that already holds ANYTHING is left alone — that covers both the app's
   * own defaults (the template composer pre-fills the footer) and the user's edits.
   * Once every field for the step is non-empty the step is recorded and never
   * touched again.
   */
  private applyFill(step: TaskStep): void {
    if (!step.fill || step.fill.length === 0 || this.filled.has(step.id)) return;

    let allPresent = true;
    for (const [selector, value] of step.fill) {
      if (isFilled(selector)) continue;
      // `onlyIfEmpty` is redundant after the check above but kept as a guard: it is
      // the invariant that stops this from ever overwriting real input.
      const wrote = fillField(selector, value, { onlyIfEmpty: true });
      if (!isFilled(selector)) {
        allPresent = false;
        if (!wrote) {
          trace(`  fill pending "${step.id}"`, selector, describe(resolveAnchor({ selector })?.element ?? null));
        }
      }
    }

    if (allPresent) {
      this.filled.add(step.id);
      trace(`  fill complete "${step.id}"`);
    }
  }

  private isSatisfied(step: TaskStep): boolean {
    if (!step.satisfied) return true;
    try {
      return step.satisfied();
    } catch (error) {
      // A throwing predicate must not wedge the flow. Treating it as unmet keeps
      // Next disabled and leaves Skip as the way past, which is recoverable.
      console.warn(`[tour-guide] task step "${step.id}": satisfied() threw`, error);
      return false;
    }
  }

  /* ---------------------------------------------------------------- *
   * State.
   * ---------------------------------------------------------------- */

  private stepViews(task: TaskFlow): TaskStepView[] {
    return task.steps.map((step, i) => {
      if (this.skipped.has(step.id)) return { id: step.id, label: step.label, state: 'skipped' };
      if (this.done.has(step.id)) return { id: step.id, label: step.label, state: 'complete' };
      if (i === this.index && !this.finished) {
        return { id: step.id, label: step.label, state: 'active' };
      }
      return { id: step.id, label: step.label, state: 'upcoming' };
    });
  }

  private rect(): TaskTargetRect | null {
    if (!this.el) return null;
    const r = this.el.getBoundingClientRect();
    // A zero box means the element is in the DOM but not laid out (a collapsed
    // container, a hidden tab). Drawing a ring there would put a dot in the
    // corner of the screen, so report nothing instead.
    if (r.width === 0 && r.height === 0) return null;
    return { top: r.top, left: r.left, width: r.width, height: r.height };
  }

  /**
   * The dialog the current target sits in, if any.
   *
   * `closest` rather than a document query: there can be more than one dialog in
   * the DOM, and the one that matters is the ancestor of the thing being pointed
   * at. Returns null on the sidebar/list steps, where there is no dialog and the
   * card is free to sit beside the control itself.
   */
  private avoidRect(): TaskTargetRect | null {
    const host = this.el?.closest('[role="dialog"], [role="alertdialog"]') as HTMLElement | null;
    if (!host) return null;
    const r = host.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return null;
    return { top: r.top, left: r.left, width: r.width, height: r.height };
  }

  /**
   * The bar, with every step still upcoming and a "we are opening it" line
   * where the chips' active marker would be.
   *
   * Separate from `publish` rather than a flag threaded through it: `publish`
   * reads the CURRENT step for almost everything it emits, and there is no
   * current step yet. It also deliberately skips the signature check — this is
   * a one-shot state change, and there is nothing for it to be deduplicated
   * against.
   */
  private publishPreparing(task: TaskFlow): void {
    this.signature = '';
    this.options.onState({
      running: true,
      taskId: task.id,
      title: task.title,
      summary: task.summary,
      doneTitle: task.doneTitle,
      doneDescription: task.doneDescription,
      preparing: true,
      preparingLabel: preparingLabelFor(task),
      index: -1,
      total: task.steps.length,
      steps: task.steps.map((step) => ({ id: step.id, label: step.label, state: 'upcoming' })),
      step: null,
      target: null,
      avoid: null,
      finished: false,
      skippedCount: 0,
      exited: false,
    });
  }

  private publish(force: boolean): void {
    const task = this.task;
    if (!task) return;

    const step = task.steps[this.index] ?? null;
    const satisfied = step ? this.isSatisfied(step) : true;
    const auto = step?.advance === 'auto';
    const target = this.rect();
    const avoid = this.avoidRect();
    const lost = !this.el && Date.now() - this.lookingSince > RESOLVE_TIMEOUT_MS;

    const state: TaskRunState = {
      running: true,
      taskId: task.id,
      title: task.title,
      summary: task.summary,
      doneTitle: task.doneTitle,
      doneDescription: task.doneDescription,
      // Always false here: `publish` only ever runs from inside a step, and
      // `enter(0)` publishing is precisely what ENDS the preparing state.
      preparing: false,
      preparingLabel: preparingLabelFor(task),
      index: this.index,
      total: task.steps.length,
      steps: this.stepViews(task),
      step:
        step && !this.finished
          ? {
              id: step.id,
              title: step.title,
              description: step.description,
              side: step.side ?? 'right',
              waiting: auto && !satisfied,
              waitingHint: step.waitingHint ?? null,
              satisfied,
              auto,
              nextLabel: step.nextLabel ?? null,
              // 🔴 A REAL LATER STEP ONLY. `skipTo: 'end'` used to render here
              // as a "Next module" button, which is now what "Skip module" does
              // on every step — see `TaskSpotlight`. Two controls doing the same
              // thing on one step, one of which appeared only sometimes, is
              // worse than either alone.
              canSkipSegment: Boolean(step.skipTo) && step.skipTo !== 'end',
              lost,
              // Only while we are genuinely still setting the step up: once the
              // target resolves, or once `lost` says it is not coming, the card
              // has to appear or the flow would have no way forward.
              awaiting: this.awaitingTarget && !this.el && !lost,
              spotlight: Boolean(step.spotlight),
            }
          : null,
      target,
      avoid,
      finished: this.finished,
      skippedCount: this.skipped.size,
      exited: false,
    };

    // Rounded so sub-pixel jitter during a smooth scroll does not re-render on
    // every one of the seven ticks a second.
    const signature = [
      state.index,
      state.finished,
      satisfied,
      lost,
      target ? Math.round(target.top) : 'x',
      target ? Math.round(target.left) : 'x',
      target ? Math.round(target.width) : 'x',
      target ? Math.round(target.height) : 'x',
      // The card is held back while this is true, so a change to it is a change
      // to what is on screen — left out of the signature, the un-hiding waited
      // for some other field to move.
      this.awaitingTarget,
      avoid ? Math.round(avoid.left) : 'x',
      avoid ? Math.round(avoid.width) : 'x',
      // 🔴 THE DIALOG'S VERTICAL BOX COUNTS TOO. The bar decides whether it has
      // to step out of the dialog's way from how far down the screen that dialog
      // reaches (`ui/barPlacement.ts`), so a dialog that grows or shrinks
      // without moving sideways — a form gaining a field, a window resized —
      // has to reach the UI. Left out, the bar kept a placement computed for a
      // box that no longer existed.
      avoid ? Math.round(avoid.top) : 'x',
      avoid ? Math.round(avoid.height) : 'x',
      this.done.size,
      this.skipped.size,
    ].join('|');

    if (!force && signature === this.signature) return;
    this.signature = signature;
    this.options.onState(state);
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
    // Removed with the same `capture` flag it was added with — omitting it leaves
    // the listener attached, and a leaked one keeps calling `publish` on a torn-
    // down engine on every scroll for the rest of the page's life.
    if (this.onViewportChange) {
      document.removeEventListener('scroll', this.onViewportChange, { capture: true });
      window.removeEventListener('resize', this.onViewportChange);
      this.onViewportChange = null;
    }
  }
}
